/**
 * What `/tv/{id}` knows about a show, cached.
 *
 * This replaces a store that kept only the airing flag. The flag came from the
 * same request as everything else here and the rest was thrown away, so the
 * sheet had no rating, no genres and no network while the app was already
 * paying for the call that carries them. One record, one request, two readers.
 *
 * The airing flag still matters most, because `not_aired_episodes_count` on
 * the library payload cannot answer "is this show still running": measured
 * against a real 193-show library it is zero for 185 of them, which put Rick
 * and Morty, The Last of Us and House of the Dragon in "Completed".
 *
 * Cached because a show's status, network and genres change on the order of
 * months, and a stale rating is a far cheaper error than a screen that takes
 * twenty seconds to classify itself.
 */
import { DETAILS_STORE, withStore } from "@/store/db";

/** Re-checked after this long. None of these fields move quickly. */
const TTL_MS = 7 * 24 * 60 * 60 * 1000;

export interface ShowDetail {
  simklId: number;
  airing: boolean;
  imdbRating: number | null;
  imdbVotes: number | null;
  genres: readonly string[];
  network: string | null;
  certification: string | null;
  overview: string | null;
  checkedAt: number;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function str(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

/**
 * Simkl embeds HTML in overviews — Succession's arrives with a literal
 * "<br><br>" in the middle of it, which rendered as those six characters on
 * screen. The markup is turned into real line breaks and any other tag is
 * dropped; it is never handed to the DOM as HTML, so a hostile overview cannot
 * become script.
 */
function plain(value: unknown): string | null {
  const raw = str(value);
  if (raw === null) return null;
  const text = raw
    .replaceAll(/<br\s*\/?>/giu, "\n")
    .replaceAll(/<[^>]*>/gu, "")
    .replaceAll(/[^\S\n]+/gu, " ")
    .replaceAll(/\n{3,}/gu, "\n\n")
    .trim();
  return text.length > 0 ? text : null;
}

function num(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/** Reads `status`. Anything unrecognised means ended, which is the safe default. */
function parseAiring(raw: Record<string, unknown>): boolean {
  const status = raw["status"];
  return status === "airing" || status === "continuing" || status === "returning";
}

function parseImdb(raw: Record<string, unknown>): { rating: number | null; votes: number | null } {
  const ratings = raw["ratings"];
  if (!isRecord(ratings)) return { rating: null, votes: null };
  const imdb = ratings["imdb"];
  if (!isRecord(imdb)) return { rating: null, votes: null };
  return { rating: num(imdb["rating"]), votes: num(imdb["votes"]) };
}

function parseGenres(raw: Record<string, unknown>): readonly string[] {
  const genres = raw["genres"];
  if (!Array.isArray(genres)) return [];
  return genres.filter((genre): genre is string => typeof genre === "string" && genre.length > 0);
}

/** Builds a record from a `/tv/{id}?extended=full` body. */
export function parseDetail(simklId: number, raw: unknown): ShowDetail {
  const body = isRecord(raw) ? raw : {};
  const imdb = parseImdb(body);
  return {
    simklId,
    airing: parseAiring(body),
    imdbRating: imdb.rating,
    imdbVotes: imdb.votes,
    genres: parseGenres(body),
    network: str(body["network"]),
    certification: str(body["certification"]),
    overview: plain(body["overview"]),
    checkedAt: Date.now(),
  };
}

function fresh(record: ShowDetail, now: number): boolean {
  return now - record.checkedAt < TTL_MS;
}

export async function readAll(): Promise<Map<number, ShowDetail>> {
  const [records] = await withStore<ShowDetail[]>(
    DETAILS_STORE,
    "readonly",
    (store) => store.getAll() as IDBRequest<ShowDetail[]>,
  );
  const now = Date.now();
  const map = new Map<number, ShowDetail>();
  for (const record of records ?? []) {
    if (fresh(record, now)) map.set(record.simklId, record);
  }
  return map;
}

export async function readOne(simklId: number): Promise<ShowDetail | null> {
  try {
    const [record] = await withStore<ShowDetail | undefined>(
      DETAILS_STORE,
      "readonly",
      (store) => store.get(simklId) as IDBRequest<ShowDetail | undefined>,
    );
    if (record === undefined || !fresh(record, Date.now())) return null;
    return record;
  } catch {
    return null;
  }
}

export async function write(records: readonly ShowDetail[]): Promise<void> {
  if (records.length === 0) return;
  try {
    await withStore(DETAILS_STORE, "readwrite", (store) =>
      records.map((record) => store.put(record)),
    );
  } catch {
    // A full or unavailable store costs a repeat request, never correctness.
  }
}
